/**
 * Dynamic Theme Composable
 *
 * This composable automatically applies client-specific theme settings when the URL
 * is under a client segment (/{clientCode}/xxxx pattern).
 *
 * Features:
 * - Automatically detects client from URL segment
 * - Fetches and applies client theme settings from API
 * - Updates CSS custom properties for colors, typography, and layout
 * - Resets to default theme when navigating away from client routes
 * - Integrates with Vuestic UI color system
 *
 * Usage:
 * - Import and initialize in App.vue
 * - Theme settings are managed in ClientSettings.vue
 * - CSS custom properties available: --client-primary-color, --client-font-family, etc.
 * - Utility classes: .text-client-primary, .border-client-accent, etc.
 */

import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useCurrentClient } from '@/composables/useCurrentClient'
import { useMsal } from '@/azure/useMsal'
import { useColors } from 'vuestic-ui'
import axiosUtil from '@/services/axios-helper'
import { Client } from '@/interfaces/client'

// Helper to load Google Fonts dynamically
const loadGoogleFont = (fontFamily: string) => {
  // Map of font family keys to Google Fonts names
  const fontMap: Record<string, string> = {
    roboto: 'Roboto:wght@300;400;500;700',
    opensans: 'Open+Sans:wght@300;400;600;700',
    lato: 'Lato:wght@300;400;700',
    poppins: 'Poppins:wght@300;400;500;600;700',
    inter: 'Inter:wght@400;600;700', // Already loaded, but include for completeness
  }

  const googleFontName = fontMap[fontFamily]

  if (!googleFontName) {
    return // Default or unknown font, no need to load
  }

  // Check if the font link already exists
  const existingLink = document.querySelector(`link[data-font="${fontFamily}"]`)
  if (existingLink) {
    return // Font already loaded
  }

  // Create and append the font link
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${googleFontName}&display=swap`
  link.setAttribute('data-font', fontFamily)
  document.head.appendChild(link)

  console.log(`Loaded Google Font: ${fontFamily}`)
}

// Standalone theme application function that can be used outside Vue setup context
export const applyClientTheme = (theme: Client['theme']) => {
  if (!theme) {
    return
  }

  const root = document.documentElement

  // Helper functions
  const getBorderRadiusValue = (radius: string) => {
    const values = {
      none: '0px',
      small: '4px',
      medium: '8px',
      large: '16px',
      full: '9999px',
    }
    return values[radius as keyof typeof values] || '8px'
  }

  const getFontFamilyValue = (family: string) => {
    const values = {
      default: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      roboto: '"Roboto", sans-serif',
      opensans: '"Open Sans", sans-serif',
      lato: '"Lato", sans-serif',
      poppins: '"Poppins", sans-serif',
      inter: '"Inter", sans-serif',
    }
    return values[family as keyof typeof values] || values.default
  }

  const getFontSizeValue = (size: string) => {
    const values = {
      small: '14px',
      medium: '16px',
      large: '18px',
    }
    return values[size as keyof typeof values] || '16px'
  }

  // Create color object for Vuestic
  const vuesticColors: Record<string, string> = {}

  // Apply colors to both CSS and Vuestic
  if (theme.primaryColor) {
    root.style.setProperty('--va-primary', theme.primaryColor)
    root.style.setProperty('--client-primary-color', theme.primaryColor)
    vuesticColors.primary = theme.primaryColor
  }

  if (theme.secondaryColor) {
    root.style.setProperty('--va-secondary', theme.secondaryColor)
    root.style.setProperty('--client-secondary-color', theme.secondaryColor)
    vuesticColors.secondary = theme.secondaryColor
  }

  if (theme.accentColor) {
    root.style.setProperty('--client-accent-color', theme.accentColor)
    vuesticColors.info = theme.accentColor
  }

  if (theme.successColor) {
    root.style.setProperty('--va-success', theme.successColor)
    root.style.setProperty('--client-success-color', theme.successColor)
    vuesticColors.success = theme.successColor
  }

  if (theme.warningColor) {
    root.style.setProperty('--va-warning', theme.warningColor)
    root.style.setProperty('--client-warning-color', theme.warningColor)
    vuesticColors.warning = theme.warningColor
  }

  if (theme.errorColor) {
    root.style.setProperty('--va-danger', theme.errorColor)
    root.style.setProperty('--client-error-color', theme.errorColor)
    vuesticColors.danger = theme.errorColor
  }

  // Apply typography
  if (theme.fontFamily) {
    const fontFamily = getFontFamilyValue(theme.fontFamily)

    // Load the Google Font if needed
    if (theme.fontFamily !== 'default') {
      loadGoogleFont(theme.fontFamily)
    }

    root.style.setProperty('--client-font-family', fontFamily)
    root.style.setProperty('--va-font-family', fontFamily)
    document.body.style.fontFamily = fontFamily
    console.log('Applied font family (standalone):', theme.fontFamily, '→', fontFamily)
  }

  if (theme.fontSize) {
    const fontSize = getFontSizeValue(theme.fontSize)
    root.style.setProperty('--client-font-size', fontSize)
    root.style.setProperty('--va-font-size-base', fontSize)
    // Apply to body for global effect
    document.body.style.fontSize = fontSize
    console.log('Applied font size (standalone):', theme.fontSize, '→', fontSize)
  }

  if (theme.borderRadius) {
    const borderRadius = getBorderRadiusValue(theme.borderRadius)
    root.style.setProperty('--client-border-radius', borderRadius)

    // Set Vuestic-specific CSS variables for common components
    root.style.setProperty('--va-button-border-radius', borderRadius)
    root.style.setProperty('--va-card-border-radius', borderRadius)
    root.style.setProperty('--va-input-wrapper-border-radius', borderRadius)
    root.style.setProperty('--va-select-border-radius', borderRadius)
    root.style.setProperty('--va-badge-border-radius', borderRadius)
    root.style.setProperty('--va-chip-border-radius', borderRadius)
    root.style.setProperty('--va-modal-border-radius', borderRadius)
    root.style.setProperty('--va-data-table-border-radius', borderRadius)

    // Inject dynamic CSS to override Vuestic component border-radius
    let styleElement = document.getElementById('dynamic-theme-border-radius')
    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = 'dynamic-theme-border-radius'
      document.head.appendChild(styleElement)
    }

    styleElement.textContent = `
      .va-card,
      .va-button,
      .va-input-wrapper,
      .va-select,
      .va-badge,
      .va-chip,
      .va-modal,
      .va-data-table,
      .va-dropdown__anchor,
      .va-dropdown__content {
        border-radius: ${borderRadius} !important;
      }
    `

    console.log('Applied border radius (standalone):', theme.borderRadius, '→', borderRadius)
  }

  // Note: Dark mode is now handled by useUserDarkMode composable
  // Client dark mode setting is ignored in favor of user preference
  // if (theme.darkMode !== undefined) {
  //   if (theme.darkMode) {
  //     document.documentElement.classList.add('dark', 'client-dark-mode')
  //     console.log('Applied dark mode classes (standalone)')
  //   } else {
  //     document.documentElement.classList.remove('dark', 'client-dark-mode')
  //     console.log('Removed dark mode classes (standalone)')
  //   }
  // }

  // Note: Vuestic colors will be updated by the main theme system
  // This standalone function is mainly for immediate preview/testing
  console.log('Would update Vuestic colors:', vuesticColors)

  console.log('Applied client theme (standalone):', theme)
}

export const useDynamicTheme = () => {
  const route = useRoute()
  const { currentClientCode } = useCurrentClient()
  const { instance } = useMsal()
  const { setColors, getColors, applyPreset } = useColors()
  const currentTheme = ref<Client['theme'] | null>(null)
  const isLoading = ref(false)

  // CSS custom properties for theme
  const applyThemeToDocument = (theme: Client['theme']) => {
    if (!theme) {
      // Reset to default theme
      resetThemeToDefault()
      return
    }

    const root = document.documentElement

    // Get current Vuestic colors to preserve non-theme colors
    const currentColors = getColors()

    // Create new color palette for Vuestic
    const newColors: Record<string, string> = { ...currentColors }

    // Apply color palette to both CSS custom properties and Vuestic colors
    if (theme.primaryColor) {
      root.style.setProperty('--va-primary', theme.primaryColor)
      root.style.setProperty('--client-primary-color', theme.primaryColor)
      newColors.primary = theme.primaryColor
    }

    if (theme.secondaryColor) {
      root.style.setProperty('--va-secondary', theme.secondaryColor)
      root.style.setProperty('--client-secondary-color', theme.secondaryColor)
      newColors.secondary = theme.secondaryColor
    }

    if (theme.accentColor) {
      root.style.setProperty('--client-accent-color', theme.accentColor)
      newColors.info = theme.accentColor // Map accent to info color
    }

    if (theme.successColor) {
      root.style.setProperty('--va-success', theme.successColor)
      root.style.setProperty('--client-success-color', theme.successColor)
      newColors.success = theme.successColor
    }

    if (theme.warningColor) {
      root.style.setProperty('--va-warning', theme.warningColor)
      root.style.setProperty('--client-warning-color', theme.warningColor)
      newColors.warning = theme.warningColor
    }

    if (theme.errorColor) {
      root.style.setProperty('--va-danger', theme.errorColor)
      root.style.setProperty('--client-error-color', theme.errorColor)
      newColors.danger = theme.errorColor
    }

    // Apply the new colors to Vuestic UI
    setColors(newColors)
    console.log('Updated Vuestic colors:', newColors)

    // Apply typography
    if (theme.fontFamily) {
      const fontFamily = getFontFamilyValue(theme.fontFamily)

      // Load the Google Font if needed
      if (theme.fontFamily !== 'default') {
        loadGoogleFont(theme.fontFamily)
      }

      root.style.setProperty('--client-font-family', fontFamily)
      root.style.setProperty('--va-font-family', fontFamily)
      // Apply to body for global effect
      document.body.style.fontFamily = fontFamily
      console.log('Applied font family:', theme.fontFamily, '→', fontFamily)
    }

    if (theme.fontSize) {
      const fontSize = getFontSizeValue(theme.fontSize)
      root.style.setProperty('--client-font-size', fontSize)
      root.style.setProperty('--va-font-size-base', fontSize)
      // Apply to body for global effect
      document.body.style.fontSize = fontSize
      console.log('Applied font size:', theme.fontSize, '→', fontSize)
    }

    if (theme.borderRadius) {
      const borderRadius = getBorderRadiusValue(theme.borderRadius)
      root.style.setProperty('--client-border-radius', borderRadius)

      // Set Vuestic-specific CSS variables for common components
      // Vuestic uses component-specific variables, not a global --va-border-radius
      root.style.setProperty('--va-button-border-radius', borderRadius)
      root.style.setProperty('--va-card-border-radius', borderRadius)
      root.style.setProperty('--va-input-wrapper-border-radius', borderRadius)
      root.style.setProperty('--va-select-border-radius', borderRadius)
      root.style.setProperty('--va-badge-border-radius', borderRadius)
      root.style.setProperty('--va-chip-border-radius', borderRadius)
      root.style.setProperty('--va-modal-border-radius', borderRadius)
      root.style.setProperty('--va-data-table-border-radius', borderRadius)

      console.log('Applied border radius:', theme.borderRadius, '→', borderRadius)
    }

    // Note: Dark mode is now handled by useUserDarkMode composable
    // Client dark mode setting is ignored in favor of user preference
    // if (theme.darkMode !== undefined) {
    //   if (theme.darkMode) {
    //     document.documentElement.classList.add('dark', 'client-dark-mode')
    //     // Apply Vuestic dark preset
    //     applyPreset('dark')
    //     console.log('Applied dark mode classes and Vuestic dark preset')
    //   } else {
    //     document.documentElement.classList.remove('dark', 'client-dark-mode')
    //     // Apply Vuestic light preset
    //     applyPreset('light')
    //     console.log('Removed dark mode classes and applied Vuestic light preset')
    //   }
    // }

    console.log('Applied client theme:', theme)

    // Debug: Check if CSS properties were actually set
    console.log('CSS Properties after applying theme:')
    console.log('--va-primary:', root.style.getPropertyValue('--va-primary'))
    console.log('--client-primary-color:', root.style.getPropertyValue('--client-primary-color'))
    console.log('--va-secondary:', root.style.getPropertyValue('--va-secondary'))
    console.log('--client-font-family:', root.style.getPropertyValue('--client-font-family'))
    console.log('--va-font-family:', root.style.getPropertyValue('--va-font-family'))
    console.log('--client-font-size:', root.style.getPropertyValue('--client-font-size'))
    console.log('--va-font-size-base:', root.style.getPropertyValue('--va-font-size-base'))
    console.log('--client-border-radius:', root.style.getPropertyValue('--client-border-radius'))
    console.log('--va-card-border-radius:', root.style.getPropertyValue('--va-card-border-radius'))
    console.log('--va-button-border-radius:', root.style.getPropertyValue('--va-button-border-radius'))
    console.log('body.style.fontFamily:', document.body.style.fontFamily)
    console.log('body.style.fontSize:', document.body.style.fontSize)
  }

  const resetThemeToDefault = () => {
    const root = document.documentElement

    // Remove custom properties
    const customProperties = [
      '--client-primary-color',
      '--client-secondary-color',
      '--client-accent-color',
      '--client-success-color',
      '--client-warning-color',
      '--client-error-color',
      '--client-font-family',
      '--client-font-size',
      '--client-border-radius',
      '--va-font-family',
      '--va-font-size-base',
      '--va-button-border-radius',
      '--va-card-border-radius',
      '--va-input-wrapper-border-radius',
      '--va-select-border-radius',
      '--va-badge-border-radius',
      '--va-chip-border-radius',
      '--va-modal-border-radius',
      '--va-data-table-border-radius',
    ]

    customProperties.forEach((prop) => {
      root.style.removeProperty(prop)
    })

    // Reset to default font and size
    document.body.style.fontFamily = ''
    document.body.style.fontSize = ''

    // Remove dynamic border-radius style element
    const styleElement = document.getElementById('dynamic-theme-border-radius')
    if (styleElement) {
      styleElement.remove()
    }

    // Remove client dark mode classes
    document.documentElement.classList.remove('client-dark-mode')

    // Note: Don't reset dark mode here as it's now managed by user preference
    // Dark mode will be managed by useUserDarkMode composable
    // document.documentElement.classList.remove('dark')
    // applyPreset('light')

    // Reset Vuestic colors to light theme defaults (including background colors)
    // This ensures consistent light theme colors even when user has toggled dark mode
    setColors({
      primary: '#154EC1',
      secondary: '#767C88',
      success: '#3D9209',
      info: '#158DE3',
      danger: '#E42222',
      warning: '#FFD43A',
      backgroundPrimary: '#F4F6F8',
      backgroundSecondary: '#FFFFFF',
      backgroundElement: '#ECF0F1',
      textPrimary: '#262824',
      textInverted: '#FFFFFF',
    })

    console.log('Reset to default theme (light mode)')
  }

  // Helper functions for CSS values (same as in ClientSettings)
  const getBorderRadiusValue = (radius: string) => {
    const values = {
      none: '0px',
      small: '4px',
      medium: '8px',
      large: '16px',
      full: '9999px',
    }
    return values[radius as keyof typeof values] || '8px'
  }

  const getFontFamilyValue = (family: string) => {
    const values = {
      default: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      roboto: '"Roboto", sans-serif',
      opensans: '"Open Sans", sans-serif',
      lato: '"Lato", sans-serif',
      poppins: '"Poppins", sans-serif',
      inter: '"Inter", sans-serif',
    }
    return values[family as keyof typeof values] || values.default
  }

  const getFontSizeValue = (size: string) => {
    const values = {
      small: '14px',
      medium: '16px',
      large: '18px',
    }
    return values[size as keyof typeof values] || '16px'
  }

  const fetchAndApplyClientTheme = async (clientCode: string) => {
    try {
      isLoading.value = true
      const response = await (await axiosUtil(instance)).get(`/api/clients/${clientCode}/public`)
      const payload = response.data as { clientCode: string; theme: Client['theme'] | null }

      currentTheme.value = payload.theme || null
      applyThemeToDocument(payload.theme || undefined)
    } catch (error) {
      console.error('Failed to fetch client theme:', error)
      // Reset to default on error
      resetThemeToDefault()
    } finally {
      isLoading.value = false
    }
  }

  const updateTheme = async () => {
    try {
      // Check if we have a current client
      if (currentClientCode.value) {
        // Fetch and apply client theme
        await fetchAndApplyClientTheme(currentClientCode.value)
      } else {
        // Reset to default theme when not in client routes
        resetThemeToDefault()
        currentTheme.value = null
      }
    } catch (error) {
      console.error('Error updating theme:', error)
      resetThemeToDefault()
    }
  }

  // Watch for route changes
  watch(() => route.path, updateTheme, { immediate: true })

  // Watch for current client changes
  watch(() => currentClientCode.value, updateTheme, { immediate: true })

  return {
    updateTheme,
    currentTheme,
    isLoading,
    applyThemeToDocument,
    resetThemeToDefault,
  }
}
